import React from 'react';
import { Box, Link } from '@mui/material';
import useMarketingFonts from '../marketing/useMarketingFonts';
import useSeoHead from '../marketing/useSeoHead';
import MarketingNav from '../marketing/components/MarketingNav';
import MarketingFooter from '../marketing/components/MarketingFooter';
import { marketingColors as C, marketingFonts as F } from '../theme_landing_page';

const Heading: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box component="h2" sx={{ fontFamily: F.display, fontWeight: 400, fontSize: 26, letterSpacing: '-0.02em', margin: '36px 0 12px' }}>
    {children}
  </Box>
);
const Body: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box sx={{ fontSize: 16, lineHeight: 1.7, color: C.body }}>{children}</Box>
);

const CookiePolicyPage: React.FC = () => {
  useMarketingFonts();
  useSeoHead({
    title: 'Cookie Policy | Landvale',
    description: 'How Landvale uses cookies and similar technologies on our website and application.',
    canonicalPath: '/cookies',
  });

  return (
    <Box className="lv-marketing" sx={{ background: C.white, color: C.ink, fontFamily: F.body, fontSize: 16, lineHeight: 1.5, overflowX: 'hidden' }}>
      <MarketingNav />
      <Box component="section" sx={{ padding: { xs: '120px 24px 64px', md: '140px 52px 96px' } }}>
        <Box sx={{ maxWidth: 760, margin: '0 auto' }}>
          <Box component="h1" sx={{ fontFamily: F.display, fontWeight: 400, fontSize: 'clamp(2.5rem,5vw,4rem)', lineHeight: 1.1, letterSpacing: '-0.04em' }}>
            Cookie Policy
          </Box>
          <Box sx={{ marginTop: '16px', color: C.muted, fontSize: 14 }}>Last updated 16 August 2026</Box>
          <Box sx={{ marginTop: '24px', fontSize: 17, lineHeight: 1.7, color: C.proseText }}>
            This policy explains how Landvale uses cookies and similar technologies on this website, and how the product itself keeps you signed in.
          </Box>

          <Heading>What we actually use</Heading>
          <Body>
            Landvale's app does not use cookies to keep you signed in — your session token is held in your browser's local storage instead,
            and is cleared when you log out. This site does not currently run any analytics, advertising or tracking cookies, so there is no
            cookie consent banner: there is nothing non-essential to ask permission for yet.
          </Body>

          <Heading>Strictly necessary storage</Heading>
          <Body>
            A small number of first-party cookies may be set by our infrastructure (for example, load-balancing or CSRF protection) purely to
            keep the site working and secure. These aren't used to identify you across sites or for advertising.
          </Body>

          <Heading>If this changes</Heading>
          <Body>
            If we ever add analytics or marketing cookies, we'll update this page first and add a consent banner so you can accept or decline
            before anything non-essential is set.
          </Body>

          <Heading>Third-party services</Heading>
          <Body>
            Signing in with Google, connecting a bank via Open Banking, or using the address finder involves those third parties (Google,
            TrueLayer, LocationIQ) directly — see our{' '}
            <Link component="a" href="/privacy" sx={{ color: C.accent }}>
              Privacy Policy
            </Link>{' '}
            for what each of them sees and why.
          </Body>

          <Heading>Contact</Heading>
          <Body>
            Questions about this policy can be sent to{' '}
            <Link component="a" href="mailto:privacy@landvale.co.uk" sx={{ color: C.accent }}>
              privacy@landvale.co.uk
            </Link>
            .
          </Body>
        </Box>
      </Box>
      <MarketingFooter />
    </Box>
  );
};

export default CookiePolicyPage;
